/**
 * THIS chat, read from Chatwoot's own store and changed through Chatwoot's own actions, with
 * the viewer's own session (spec P-1.6). No BFF: there is no new permission surface, and the
 * store is kept live by ActionCable, so the row never lags behind what another agent did.
 *
 * Chatwoot's `toggleStatus` and `assignAgent` actions catch their own errors, so what they
 * RETURN says nothing. The two writes below answer with what the store holds afterwards.
 *
 * `getCurrentInstance()`, not `useStore()`: the pane is mounted by specs with no store, and
 * `useStore()` throws there. Same escape hatch as `useWasloAccountId`.
 */
import { computed, getCurrentInstance } from 'vue';

import { NEEDS_A_PERSON_LABEL } from '../helpers/chat-ownership.js';

/**
 * @param {import('vue').Ref<number|null>} conversationId
 */
export const useChatOwnership = conversationId => {
  const store = getCurrentInstance()?.proxy?.$store ?? null;

  const chat = computed(() => {
    const byId = store?.getters?.getConversationById;
    if (conversationId.value === null || typeof byId !== 'function')
      return null;
    return byId(conversationId.value) ?? null;
  });

  const me = computed(() => {
    const id = store?.getters?.getCurrentUserID;
    return typeof id === 'number' ? id : null;
  });

  const status = computed(() => chat.value?.status ?? null);

  /** A PERSON holding the chat. A bot-owned chat reports the AgentBot, which is not one. */
  const assignee = computed(() => {
    const meta = chat.value?.meta;
    const person = meta?.assignee;
    if (!person || typeof person.id !== 'number') return null;
    if (meta.assignee_type && meta.assignee_type !== 'User') return null;
    return { id: person.id, name: person.available_name || person.name || '' };
  });

  const labels = () => {
    const read = store?.getters?.['conversationLabels/getConversationLabels'];
    const fromStore =
      typeof read === 'function' ? read(conversationId.value) : null;
    if (Array.isArray(fromStore)) return fromStore;
    return Array.isArray(chat.value?.labels) ? chat.value.labels : [];
  };

  /** "Handed over, nobody has answered yet" stops being true the moment someone takes it. */
  const dropLabel = async () => {
    const current = labels();
    if (!current.includes(NEEDS_A_PERSON_LABEL)) return;
    await store.dispatch('conversationLabels/update', {
      conversationId: conversationId.value,
      labels: current.filter(label => label !== NEEDS_A_PERSON_LABEL),
    });
  };

  /** "I'll take this one": open, assigned to the viewer, the label off. */
  const takeOver = async () => {
    if (store === null || conversationId.value === null) return false;
    await store.dispatch('toggleStatus', {
      conversationId: conversationId.value,
      status: 'open',
    });
    if (me.value !== null) {
      await store.dispatch('assignAgent', {
        conversationId: conversationId.value,
        agentId: me.value,
      });
    }
    await dropLabel();
    return status.value === 'open';
  };

  /** "Give it back": pending again, the assignee kept, the label off. */
  const giveBack = async () => {
    if (store === null || conversationId.value === null) return false;
    await store.dispatch('toggleStatus', {
      conversationId: conversationId.value,
      status: 'pending',
    });
    await dropLabel();
    return status.value === 'pending';
  };

  return {
    hasStore: store !== null,
    chat,
    me,
    status,
    assignee,
    takeOver,
    giveBack,
  };
};

export default useChatOwnership;
